import type { AffiliateProfile, KycStatus, RecruiterProfile } from "../types/api";
import { INTAKE_CHANNEL_WEBSITE } from "../constants/matching";
import { APPLY_PATH } from "../constants/publicUrls";

export const CONFIDENTIAL_CLIENT_NAME = "Confidential client";

export const IDENTITY_KYC_SLOT_LABELS: Record<string, string> = {
  selfie: "Live photo",
  aadhaar_front: "Aadhaar front",
  aadhaar_back: "Aadhaar back",
  pan_front: "PAN front",
  pan_back: "PAN back",
  aadhaar_number: "Aadhaar number",
  pan: "PAN",
  resume: "Resume",
};

export const KYC_REVIEW_READY = "Ready";
export const KYC_REVIEW_MISSING = "Missing";

export const IDENTITY_KYC_REQUIRED_SLOTS = [
  "selfie",
  "aadhaar_front",
  "aadhaar_back",
  "pan_front",
  "pan_back",
] as const;

export const KYC_DOCS_COLUMN_LABEL = "Documents";
export const KYC_DOCS_PROGRESS_PREFIX = "Doc";

export function kycDocumentProgressLabel(
  filled = 0,
  required: number = IDENTITY_KYC_REQUIRED_SLOTS.length,
): string {
  return `${KYC_DOCS_PROGRESS_PREFIX}: ${filled}/${required}`;
}

export type IdentityKycRole = "recruiter" | "affiliate";

export const JOIN_HUB_COPY = {
  heading: "How will you use AxioHire?",
  subheading: "Pick a path. You can sign in right away; marketplace names unlock after KYC or company verification.",
  heroTitleLead: "Choose your path on",
  heroSubtitle:
    "Looking for a job, filling live roles, or posting hiring requirements — start with the path that fits you.",
  loginPrompt: "Already have an account?",
  loginLabel: "Log in",
  continueLabel: "Continue",
  backLabel: "Back",
  youCanLabel: "What you can do",
  requiredLabel: "Required for registration",
  fillStepHeading: "Fill hiring requirements",
  fillStepSubheading: "Choose how you will source or submit against live jobs.",
  highlights: [
    "Recruiters earn 70–80% on confirmed placements",
    "Companies post roles after company verification",
    "Admin review of KYC is the marketplace access grant",
  ],
  fill: {
    title: "Fill Hiring Requirements",
    description: "Source, refer, or run a desk against live jobs. Client names unlock after KYC or company verification.",
  },
  post: {
    to: "/join/company",
    title: "Post Hiring Requirements",
    description: "Create your company, post roles, and review recruiter submissions. Publishing waits on verification and fee review.",
  },
  candidate: {
    to: `${APPLY_PATH}?ch=${INTAKE_CHANNEL_WEBSITE}`,
    title: "Get Hired",
    description:
      "Apply once and get contacted by recruiters and consultancies about jobs that match your profile.",
  },
  recruiter: {
    to: "/join/recruiter",
    title: "Recruiter",
    hubTitle: "Recruiter",
    subtitle: "Submit candidates & earn",
    description:
      "Source and screen talent for high-volume roles. Complete KYC to see client names and submit.",
    youCan: [
      "Submit candidates to live jobs and earn a placement share",
      "See client names and full JDs after admin KYC",
      "Share intake links and use Explore after KYC is verified",
    ],
    required: [
      "Account: name, email, phone, segments, and terms",
      "Then selfie, Aadhaar, PAN, and resume for admin review",
    ],
  },
  affiliate: {
    to: "/join/affiliate",
    title: "Affiliate",
    hubTitle: "Affiliates",
    subtitle: "Refer talent & earn",
    description:
      "Share your intake link. Earn a sourcing fee when referred candidates get placed. Same identity KYC as recruiters.",
    youCan: [
      "Share one intake link and earn a sourcing fee on hire",
      "Browse jobs with redacted client names until KYC is verified",
      "Keep phones for candidates you referred",
    ],
    required: [
      "Account: name, email, phone, and terms",
      "Then the same identity pack as recruiters (selfie, Aadhaar, PAN — no resume)",
    ],
  },
} as const;

export const AUTH_IDENTITY_COPY = {
  sectionTitle: "Verify your identity",
  joinDescription: "Sign in with Apple or Google, or create an email account below.",
  // loginDescription: "Sign in with Google, or use email and password.",
  loginDescription: "",
  googleOnlyDescription: "Sign in with Google to link your account.",
  emailOnlyDescription: "Sign in with Email Password to create your account.",
  orLabel: "or",
  emailButton: "Sign in with Email Password",
  appleButton: "Continue with Apple",
  googleConnectedTitle: "Google connected",
  googleConnectedBody: "Complete the form below to continue.",
  appleConnectedTitle: "Apple ID connected",
  appleConnectedBody: "Complete the form below to continue.",
  googleSignupFailed: "Google signup did not return an account. Try again.",
  appleSignupFailed: "Apple signup did not return an account. Try again.",
  appleSignInFailed: "Sign in with Apple failed. Try again.",
  pendingAccountTitle: "Account not active",
  pendingAccountBody: "This account is not active. Create a company, or ask an admin to add you.",
} as const;

export const JOIN_AFFILIATE_COPY = {
  heroSubtitle:
    "Share your AxioHire link with everyone you know. Complete KYC so client names and payouts unlock.",
  intro:
    "Create your account, then complete selfie, Aadhaar, and PAN. You can browse redacted jobs while you wait.",
  welcome: "Your affiliate account is ready. Complete KYC to share links and see client names.",
};

export const IDENTITY_KYC_CALLOUT = {
  docsMissingTitle: "Upload ID documents before your next payout",
  docsMissingBody:
    "Your marketplace access stays open. We still need a selfie, Aadhaar, and PAN on file for TDS and audits.",
  docsMissingCta: "Upload documents",
  submittedTitle: "KYC submitted — waiting for review",
  submittedRecruiterBody:
    "You can browse live jobs and pricing now. Company names and submit stay locked until an admin approves.",
  submittedAffiliateBody:
    "You can browse live jobs and pricing now. Company names and share links stay locked until an admin approves.",
  browseJobsCta: "Browse live jobs",
  rejectedTitle: "KYC rejected",
  rejectedFallback: "Replace the failed files and resubmit.",
  rejectedCta: "Fix and resubmit",
  pendingRecruiterBody:
    "Browse jobs and build your bench now. Complete KYC to see the employer and submit candidates.",
  pendingAffiliateBody:
    "Browse jobs now. Complete KYC to see the employer and share your candidate link.",
  completeCta: "Complete KYC",
};

export const IDENTITY_KYC_SHARE_BLOCK = {
  rejected: "Your KYC was rejected. Contact support before sharing candidate links.",
  unverified: "Your KYC must be approved before you can share candidate links.",
};

export const IDENTITY_KYC_PAGE = {
  recruiterTitle: "Recruiter KYC",
  affiliateTitle: "Affiliate KYC",
  recruiterHero: "then start earning",
  affiliateHero: "then start referring",
  recruiterSubtitle:
    "Camera or upload. We keep Aadhaar as last-4 plus a hash — ops review the images. You can browse live jobs while you wait.",
  affiliateSubtitle:
    "Same identity pack as recruiters: selfie, Aadhaar, and PAN. You can browse redacted jobs while you wait.",
  recruiterHighlightResume: "Selfie, Aadhaar, PAN, and resume — a few minutes on your phone",
  affiliateHighlightDocs: "Selfie, Aadhaar, and PAN — a few minutes on your phone",
  browseBeforeApproval: "Browse jobs and pricing before approval",
  recruiterUnlock: "Submit, Explore calling, and intake links unlock after Approve",
  affiliateUnlock: "Share links and payouts unlock after Approve",
  recruiterHome: "Recruiter home",
  affiliateHome: "Affiliate home",
  waitingBody: "An admin will review your documents. Browse jobs with redacted client names and pricing in the meantime.",
  legacyVerifiedRecoveryTitle: "Approval active — documents still needed",
  legacyVerifiedRecoveryBody:
    "Your existing access stays active. Upload the missing identity documents to complete your records.",
  legacyVerifiedReviewDescription:
    "Your approval stays active. Add the missing identity documents and save the completed pack.",
  legacyVerifiedSubmitLabel: "Save documents",
  submitErrorTitle: "Could not submit",
  verifiedBody: "You’re verified. Company names and submit are unlocked.",
  verifiedAffiliateBody: "You’re verified. Company names and share links are unlocked.",
};

export function maskedCompanyName(job: { company_masked?: boolean; company_name: string }): string {
  return job.company_masked ? CONFIDENTIAL_CLIENT_NAME : job.company_name;
}

export function identityMarketplaceOpen(kycStatus?: KycStatus | null): boolean {
  return kycStatus === "verified";
}

export function identityKycPackEditable(
  status?: KycStatus | null,
  documentsComplete?: boolean,
): boolean {
  return status === "pending" || status === "rejected" || (status === "verified" && documentsComplete === false);
}

export function identityNeedsKycPack(profile?: RecruiterProfile | AffiliateProfile | null): boolean {
  if (!profile) return false;
  if ("kyc_documents_complete" in profile) {
    return Boolean(profile && profile.kyc_documents_complete === false);
  }
  return profile.kyc_status !== "verified";
}

export function identityKycNavLabel(status?: KycStatus | null): string {
  if (status === "submitted") return "KYC approval pending";
  if (status === "rejected") return "Fix KYC";
  return "Complete KYC";
}

export function recruiterMarketplaceOpen(profile?: RecruiterProfile | null): boolean {
  return profile?.kyc_status === "verified";
}

export function recruiterNeedsKycPack(profile?: RecruiterProfile | null): boolean {
  return Boolean(profile && !profile.kyc_documents_complete);
}

export function recruiterProfileEditable(profile?: RecruiterProfile | null): boolean {
  return profile?.kyc_status === "pending";
}

export function recruiterKycNavLabel(profile?: RecruiterProfile | null): string {
  if (!profile) return "Complete KYC";
  if (profile.kyc_status === "verified") {
    return recruiterNeedsKycPack(profile) ? "Upload ID docs" : "Complete KYC";
  }
  if (profile.kyc_status === "submitted") return "KYC approval pending";
  if (profile.kyc_status === "rejected") return "Fix KYC";
  return "Complete KYC";
}

export function recruiterHasExploreSeat(profile?: RecruiterProfile | null): boolean {
  return Boolean(profile?.has_calling_seat);
}

export function exploreCallingUnlocked(
  profile?: RecruiterProfile | null,
  identityVerified?: boolean,
): boolean {
  return (
    recruiterHasExploreSeat(profile) &&
    recruiterMarketplaceOpen(profile) &&
    Boolean(identityVerified)
  );
}

export function exploreLockCopy(
  profile?: RecruiterProfile | null,
  identityVerified?: boolean,
): {
  alertTitle: string;
  alertBody: string;
  emptyTitle: string;
  emptyDescription: string;
  ctaLabel: string;
  ctaTo: string;
} {
  if (!identityVerified) {
    return {
      alertTitle: "Verify your contact details before calling",
      alertBody:
        "You have an Explore seat. Verify email and phone (phone only if you signed in with Google) to claim and call.",
      emptyTitle: "Contact verification required",
      emptyDescription: "Calling stays locked until your identity is verified.",
      ctaLabel: "Verify contact",
      ctaTo: "/kyc",
    };
  }
  const kyc = exploreKycLockCopy(profile?.kyc_status);
  return { ...kyc, ctaTo: "/kyc" };
}

export function exploreKycLockCopy(status: KycStatus | undefined): {
  alertTitle: string;
  alertBody: string;
  emptyTitle: string;
  emptyDescription: string;
  ctaLabel: string;
} {
  if (status === "submitted") {
    return {
      alertTitle: "KYC approval pending",
      alertBody:
        "You can still browse jobs. Explore calling unlocks after an admin approves your documents.",
      emptyTitle: "KYC approval pending",
      emptyDescription:
        "Your documents are in review. Browse jobs is still open — calling unlocks after approval.",
      ctaLabel: "View KYC status",
    };
  }
  if (status === "rejected") {
    return {
      alertTitle: "KYC rejected — update and resubmit",
      alertBody: "Fix the documents an admin flagged, then resubmit before using Explore calling.",
      emptyTitle: "KYC needs fixes before Explore",
      emptyDescription: "Replace the failed files and resubmit. Browse jobs stays open while you fix this.",
      ctaLabel: "Fix KYC",
    };
  }
  return {
    alertTitle: "Explore calling is locked until KYC is approved",
    alertBody: "You can still browse jobs. Complete KYC to claim and call from the shared pool.",
    emptyTitle: "KYC required for Explore calling",
    emptyDescription: "Browse jobs is still open. Submit and calling unlock after an admin approves your documents.",
    ctaLabel: "Complete KYC",
  };
}

export function kycStatusLabel(status: string | undefined, docsComplete?: boolean): string {
  if (status === "verified") {
    return docsComplete === false ? "Legacy verified · docs missing" : "Verified";
  }
  if (status === "submitted") return "Submitted";
  if (status === "rejected") return "Rejected";
  if (status === "pending" && docsComplete) return "Needs review";
  return "Not started";
}
